// ============================================================
// InstallPrompt — 「加到主畫面」提示 Banner
// ============================================================
// 兩種模式：
//   - Android / Chrome：監聽 beforeinstallprompt → 主動彈出安裝
//   - iOS Safari：偵測到後顯示「點分享 → 加入主畫面」指引
// ============================================================

import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const LS_KEY = 'tianshui_install_dismissed';

function isIOS(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIos, setShowIos] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // 已經裝了
    if (localStorage.getItem(LS_KEY) === '1') { setDismissed(true); return; }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari 沒有 beforeinstallprompt，用 UA 偵測
    if (isIOS()) {
      setShowIos(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      setDeferred(null);
    }
  };

  const dismiss = () => {
    localStorage.setItem(LS_KEY, '1');
    setDismissed(true);
  };

  if (dismissed) return null;
  if (!deferred && !showIos) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 z-40 max-w-md mx-auto bg-white rounded-xl shadow-xl border border-morandi-200 p-4 animate-slide-up">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-earth-200 flex items-center justify-center text-earth-600 font-bold shrink-0">
          天
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-morandi-800">加入主畫面</div>
          {deferred && (
            <div className="text-xs text-morandi-500 mt-0.5">
              裝成 App 後打卡更快，免開瀏覽器
            </div>
          )}
          {showIos && !deferred && (
            <div className="text-xs text-morandi-500 mt-0.5">
              Safari 點下方「分享 <span className="inline-block px-1">↗</span>」→「加入主畫面」
            </div>
          )}
        </div>
        {deferred && (
          <button
            onClick={handleInstall}
            className="px-3 py-1.5 bg-earth-400 hover:bg-earth-500 text-white text-xs rounded-btn shrink-0"
          >
            安裝
          </button>
        )}
        <button
          onClick={dismiss}
          className="text-morandi-300 hover:text-morandi-500 text-xl leading-none px-1 shrink-0"
          aria-label="關閉"
        >×</button>
      </div>
    </div>
  );
}
